<template>
  <div
    v-if="area || drawingArea"
    id="videoArea"
    ref="videoarea"
    class="videoArea"
    :class="{ videoArea_activating: drawingArea.isActive }"
  >
    <div
      :area-id="drawingArea.id"
      class="videoArea_select"
      :class="{
        'videoArea_select-dragging': isResizing,
        'videoArea_select-moving': isMoving,
        'multiple-area': drawingArea,
      }"
      :style="{
        top: top + '%',
        left: left + '%',
        right: right + '%',
        bottom: bottom + '%',
      }"
      @mousedown="startMoving"
      @touchstart="startMoving"
      @touchend="stopDragging"
    >
      <div
        v-if="!drawingArea || (drawingArea && drawingArea.isActive)"
        class="videoArea_corner"
        @mousedown.stop.prevent="startResizing('tl', $event)"
      />
      <div
        v-if="!drawingArea || (drawingArea && drawingArea.isActive)"
        class="videoArea_corner videoArea_corner-right"
        @mousedown.stop.prevent="startResizing('tr', $event)"
      />
      <div
        v-if="!drawingArea || (drawingArea && drawingArea.isActive)"
        class="videoArea_corner videoArea_corner-bottom"
        @mousedown.stop.prevent="startResizing('bl', $event)"
      />
      <div
        v-if="!drawingArea || (drawingArea && drawingArea.isActive)"
        class="videoArea_corner videoArea_corner-bottom videoArea_corner-right"
        @mousedown.stop.prevent="startResizing('br', $event)"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { calculatePolygans } from '@/utils/helpers';
function addEvents(events) {
  events.forEach((cb, eventName) => {
    document.documentElement.addEventListener(eventName, cb);
  });
}

function removeEvents(events) {
  events.forEach((cb, eventName) => {
    document.documentElement.removeEventListener(eventName, cb);
  });
}
export default {
  name: 'VideoArea',
  props: {
    drawingArea: {
      type: Object,
      default: () => {},
    },
    videoMetadata: {
      type: Object,
      default: () => ({ width: 1280, height: 720 }),
    },
  },
  data: () => ({
    currentCorner: null,
    isResizing: false,
    isMoving: false,
    test: '25',
    top: 25,
    left: 25,
    right: 25,
    bottom: 25,
    anchorX: 0,
    anchorY: 0,
    clientWidth: 0,
    clientHeight: 0,
    domEvents: null,
    leftBeforeMove: 0,
    topBeforeMove: 0,
    rightBeforeMove: 0,
    bottomBeforeMove: 0,
    widthBeforeMove: 0,
    heightBeforeMove: 0,
    minSize: 10,
  }),
  computed: {
    ...mapState({
      area: (state) => state.common.area,
      areaCoordinates: (state) => state.common.areaCoordinates,
    }),
    videoMetaHeight() {
      return this.videoMetadata.height ? this.videoMetadata.height : 1280;
    },
    videoMetaWidth() {
      return this.videoMetadata.width ? this.videoMetadata.width : 720;
    },
  },
  mounted() {
    if (this.drawingArea && this.drawingArea.coordinates) {
      const { top, left, bottom, right } = this.drawingArea.coordinates;
      this.left = left;
      this.bottom = bottom;
      this.right = right;
      this.top = top;
    } else if (this.drawingArea && this.drawingArea.polygonstd) {
      // eslint-disable-next-line no-unused-vars
      const [x1, y1, x2, y2, x3, y3, x4, y4] = this.drawingArea.polygonstd;

      const widthRatio = 9999 / this.videoMetaWidth; // 9999 / A
      const heightRatio = 9999 / this.videoMetaHeight; // 9999 / B

      const leftInPixel = x1 / widthRatio;
      const bottomInPixel = y3 / heightRatio;
      const rightInPixel = x2 / widthRatio;
      const topInPixel = y1 / heightRatio;

      this.left = (leftInPixel * 100) / this.videoMetaWidth;
      this.bottom = 100 - (bottomInPixel * 100) / this.videoMetaHeight;
      this.right = 100 - (rightInPixel * 100) / this.videoMetaWidth;
      this.top = (topInPixel * 100) / this.videoMetaHeight;
    }
    this.leftBeforeMove = +this.left;
    this.bottomBeforeMove = +this.bottom;
    this.rightBeforeMove = +this.right;
    this.topBeforeMove = +this.top;
    this.widthBeforeMove = 100 - (this.right + this.left);
    this.heightBeforeMove = 100 - (this.top + this.bottom);
    this.domEvents = new Map([
      ['mousemove', this.dragging],
      ['mouseup', this.stopDragging],
      ['mouseleave', this.stopDragging],
      ['touchmove', this.dragging],
      ['touchend', this.stopDragging],
      ['touchcancel', this.stopDragging],
      ['touchstart', this.stopDragging],
    ]);
    addEvents(this.domEvents);
  },
  beforeDestroy() {
    removeEvents(this.domEvents);
  },
  methods: {
    saveDimensionsBeforeMove({ pointerX, pointerY }) {
      this.clientWidth = this.$refs.videoarea.clientWidth;
      this.clientHeight = this.$refs.videoarea.clientHeight;
      this.anchorX = pointerX;
      this.anchorY = pointerY;
      this.leftBeforeMove = +this.left;
      this.bottomBeforeMove = +this.bottom;
      this.rightBeforeMove = +this.right;
      this.topBeforeMove = +this.top;
      this.widthBeforeMove = 100 - (this.right + this.left);
      this.heightBeforeMove = 100 - (this.top + this.bottom);
    },
    startMoving(ev) {
      if (typeof ev.stopPropagation !== 'undefined') {
        ev.stopPropagation();
      }

      if (typeof ev.preventDefault !== 'undefined') {
        ev.preventDefault();
      }

      const pointerX =
        typeof ev.pageX !== 'undefined' ? ev.pageX : ev.touches[0].pageX;
      const pointerY =
        typeof ev.pageY !== 'undefined' ? ev.pageY : ev.touches[0].pageY;
      this.saveDimensionsBeforeMove({ pointerX, pointerY });
      this.isMoving = true;
    },
    startResizing(corner, ev) {
      this.currentCorner = corner;
      const pointerX =
        typeof ev.pageX !== 'undefined' ? ev.pageX : ev.touches[0].pageX;
      const pointerY =
        typeof ev.pageY !== 'undefined' ? ev.pageY : ev.touches[0].pageY;
      this.saveDimensionsBeforeMove({ pointerX, pointerY });
      this.isResizing = true;
    },
    dragging(ev) {
      ev.stopPropagation();
      const pageX =
        typeof ev.pageX !== 'undefined' ? ev.pageX : ev.touches[0].pageX;
      const pageY =
        typeof ev.pageY !== 'undefined' ? ev.pageY : ev.touches[0].pageY;
      const delta = {
        x: pageX - this.anchorX,
        y: pageY - this.anchorY,
      };
      if (this.isResizing) {
        this.cornerMove(delta);
      }
      if (this.isMoving) {
        this.bodyMove(delta);
      }
    },
    cornerMove(delta) {
      let newTop = this.topBeforeMove;
      let newBottom = this.bottomBeforeMove;
      let newLeft = this.leftBeforeMove;
      let newRight = this.rightBeforeMove;
      const moveX = (delta.x / this.clientWidth) * 100;
      const moveY = (delta.y / this.clientHeight) * 100;
      switch (this.currentCorner[0]) {
        case 'b':
          newBottom = this.bottomBeforeMove - moveY;
          break;
        case 't':
          newTop = this.topBeforeMove + moveY;
          break;
        default:
          break;
      }

      switch (this.currentCorner[1]) {
        case 'r':
          newRight = this.rightBeforeMove - moveX;
          break;
        case 'l':
          newLeft = this.leftBeforeMove + moveX;
          break;
        default:
          break;
      }
      this.limitSidesCornerMove(newLeft, newRight, newTop, newBottom);
    },
    bodyMove(delta) {
      const moveX = (delta.x / this.clientWidth) * 100;
      const moveY = (delta.y / this.clientHeight) * 100;
      const left = this.leftBeforeMove + moveX;
      const right = this.rightBeforeMove - moveX;
      const top = this.topBeforeMove + moveY;
      const bottom = this.bottomBeforeMove - moveY;
      this.limitSidesBodyMove(left, right, top, bottom);
    },
    limitSidesCornerMove(left, right, top, bottom) {
      this.left = left < 0 ? 0 : left;
      this.right = right < 0 ? 0 : right;
      this.top = top < 0 ? 0 : top;
      this.bottom = bottom < 0 ? 0 : bottom;
      const onePixelWidth = 100 / this.videoMetaWidth;
      const onePixelHeight = 100 / this.videoMetaHeight;
      const width = 100 - (this.right + this.left);
      const height = 100 - (this.top + this.bottom);
      const minHeight = this.minSize * onePixelHeight;
      const minWidth = this.minSize * onePixelWidth;

      if (height < minHeight) {
        if (this.currentCorner[0] === 't') {
          this.top = 100 - (minHeight + this.bottom);
        }
        if (this.currentCorner[0] === 'b') {
          this.bottom = 100 - (minHeight + this.top);
        }
      }
      if (width < minWidth) {
        if (this.currentCorner[1] === 'l') {
          this.left = 100 - (minWidth + this.right);
        }
        if (this.currentCorner[1] === 'r') {
          this.right = 100 - (minWidth + this.left);
        }
      }
    },
    limitSidesBodyMove(left, right, top, bottom) {
      if (right < 0) {
        this.right = 0;
        this.left = 100 - this.widthBeforeMove;
      }
      if (left < 0) {
        this.right = 100 - this.widthBeforeMove;
        this.left = 0;
      }
      if (top < 0) {
        this.top = 0;
        this.bottom = 100 - this.heightBeforeMove;
      }
      if (bottom < 0) {
        this.top = 100 - this.heightBeforeMove;
        this.bottom = 0;
      }
      if (left > 0 && right > 0) {
        this.left = left;
        this.right = right;
      }
      if (top > 0 && bottom > 0) {
        this.top = top;
        this.bottom = bottom;
      }
    },
    stopDragging() {
      if (this.isResizing || this.isMoving) {
        if (this.isResizing) {
          this.isResizing = false;
        }
        if (this.isMoving) {
          this.isMoving = false;
        }
        // Adapt coordinates to 720*1280
        // The coordinates are calculated in precents, so they are between 0 and 100
        // (100 - x) is the same as x but in reverse direction of axis start point to end point
        // to calculate the coordinates in pixels, we need to multiply the precents by the width of the video
        const leftBottomX = Math.round(this.left * 12.8); // a4
        const leftBottomY = Math.round((100 - this.bottom) * 7.2); // b4
        const rightTopX = Math.round((100 - this.right) * 12.8); // a2
        const rightTopY = Math.round(this.top * 7.2); // b2
        const polygonstdSearch = [
          leftBottomX,
          rightTopY,
          rightTopX,
          leftBottomY,
        ];

        // Set coordinates to store
        this.changeAreaCoordinates(polygonstdSearch);

        const { polygonstd, coordinates } = calculatePolygans({
          videoMetaWidth: this.videoMetaWidth,
          videoMetaHeight: this.videoMetaHeight,
          top: this.top,
          left: this.left,
          bottom: this.bottom,
          right: this.right,
        });
        // eslint-disable-next-line vue/no-mutating-props
        this.drawingArea.polygonstd = [...polygonstd];
        // eslint-disable-next-line vue/no-mutating-props
        this.drawingArea.coordinates = {
          ...coordinates,
        };
      }
    },

    ...mapActions('common', ['changeAreaCoordinates']),
  },
};
</script>

<style lang="scss">
.videoArea {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 8;
  overflow: hidden;
  height: 100%;
  &_select {
    position: absolute;
    top: 25%;
    right: 25%;
    bottom: 25%;
    left: 25%;
    outline: 1000px solid rgba(0, 0, 0, 0.3);
    cursor: grab;
    &-dragging {
      pointer-events: none;
    }
    &-moving {
      cursor: grabbing;
    }
    &.multiple-area {
      outline: 3px solid #fcc800;
      .videoArea_corner {
        width: 10px;
        height: 10px;
        object-fit: contain;
        border: solid 3px #fcc800;
        background-color: #4b4b4b;
        &-right {
          right: -6px;
        }
        &-bottom {
          bottom: -6px;
        }
      }
    }
  }
  &_corner {
    position: absolute;
    background-color: #fff;
    width: 16px;
    height: 16px;
    border-radius: 8px;
    top: -8px;
    left: -8px;
    cursor: nw-resize;
    &-right {
      left: auto;
      right: -8px;
      cursor: ne-resize;
    }
    &-bottom {
      top: auto;
      bottom: -8px;
      cursor: sw-resize;
      &.videoArea_corner-right {
        cursor: se-resize;
      }
    }
  }
  &_activating {
    z-index: 10;
  }
}
</style>
